import { InfoOutlined } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { CSVField } from 'hooks/useExportCSV'
import React, { useMemo } from 'react'
import { formatTeamTypeLabel, OrgStateUnitData, TeamInfo, TeamMember, TeamType } from 'types'
import { formatDate } from 'utils'
import { TeamsMembersCell } from '../TeamsMembersCell/TeamsMembersCell'

export function useTeamsPageColumns(isOrg: boolean, onRefetch?: () => void) {
  const columnDefs: ColDef[] = useMemo(() => {
    const teamCreatorColumn: ColDef = {
      headerName: 'Team Creator',
      field: 'ownerFirstname',
      minWidth: 400,
      cellRenderer: (
        props: ICellRendererParams<{
          ownerFirstname: string
          ownerLastname: string
          ownerEmail: string
        }>,
      ) => {
        if (!props.data) return PLACEHOLDER

        const { ownerFirstname, ownerLastname, ownerEmail } = props.data

        return `${ownerFirstname} ${ownerLastname}, ${ownerEmail}`
      },
    }

    const columns: ColDef[] = [
      {
        headerName: 'Team ID',
        field: 'id',
        hide: isOrg,
      },
      {
        headerName: 'Team Name',
        field: 'name',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Team Type',
        field: 'type',
        // Fits the longest label with its abbreviation, e.g. `Personal Support Team (PST)`.
        minWidth: 270,
        cellRenderer: (
          props: ICellRendererParams<Pick<TeamInfo, 'type' | 'customTypeDescription'>>,
        ) => {
          if (!props.data) return PLACEHOLDER

          const { type, customTypeDescription } = props.data
          const isCustomized = type === TeamType.CustomizedTeam

          return (
            <Stack alignItems="center" direction="row" height="100%">
              <Typography>{formatTeamTypeLabel(type)}</Typography>
              {isCustomized && customTypeDescription && (
                <Tooltip title={customTypeDescription} placement="right" arrow>
                  <IconButton size="small">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )
        },
      },
      ...(!isOrg ? [teamCreatorColumn] : []),
      {
        headerName: 'Team Members',
        field: 'members',
        cellRenderer: (
          props: ICellRendererParams<{
            id: number
            members: TeamMember[]
          }>,
        ) => {
          if (!props.data) return PLACEHOLDER

          const { id, members } = props.data
          if (members) {
            if (members.length === 0) {
              return PLACEHOLDER
            }
            return <TeamsMembersCell members={members} teamId={id} onRefetch={onRefetch} />
          }

          return members || PLACEHOLDER
        },
      },
      {
        headerName: 'Creation Date',
        field: 'createdAt',
        cellRenderer: columnRenderers.createdAt,
      },
      {
        headerName: 'State/UNIT',
        field: 'accessCodeId',
        cellRenderer: (props: ICellRendererParams<OrgStateUnitData>) => {
          const { accessCodeSpecificState, accessCodeSpecificUnitName } = props.data || {}
          const parts = [accessCodeSpecificState, accessCodeSpecificUnitName].filter(Boolean)
          return parts.join('/') || PLACEHOLDER
        },
      },
      {
        headerName: 'Organization Name',
        field: 'organizationName',
        filter: 'agTextColumnFilter',
        flex: 1,
      },
      ...(isOrg ? [teamCreatorColumn] : []),
    ]

    return columns
  }, [isOrg, onRefetch])

  const csvFields: CSVField[] = useMemo(() => {
    const teamIdField: CSVField = {
      label: 'Team ID',
      value: 'id',
    }

    const teamCreatorField: CSVField = {
      label: 'Team Creator',
      value: (data: TeamInfo) => `${data.ownerFirstname} ${data.ownerLastname}, ${data.ownerEmail}`,
    }

    return [
      ...(!isOrg ? [teamIdField] : []),
      {
        label: 'Team Name',
        value: 'name',
      },
      {
        label: 'Team Type',
        value: (data: TeamInfo) => formatTeamTypeLabel(data.type, data.customTypeDescription),
      },
      ...(!isOrg ? [teamCreatorField] : []),
      {
        label: 'Team Members',
        value: (data: TeamInfo) =>
          data.members.map((member) => `${member.firstName} ${member.lastName}`).join(', '),
      },
      {
        label: 'Creation Date',
        value: (data: TeamInfo) => formatDate(data.createdAt),
      },
      {
        label: 'State/UNIT',
        value: (data: OrgStateUnitData) => {
          const { accessCodeSpecificState, accessCodeSpecificUnitName } = data
          const parts = [accessCodeSpecificState, accessCodeSpecificUnitName].filter(Boolean)
          return parts.join('/') || PLACEHOLDER
        },
      },
      {
        label: 'Organization Name',
        value: 'organizationName',
      },
      ...(isOrg ? [teamCreatorField] : []),
    ]
  }, [isOrg])

  return {
    columnDefs,
    csvFields,
  }
}
